import classNames from 'classnames';
import React from 'react';

import { Icon, IconTypes } from '@eika/icon';
import { ErrorLabel } from '@eika/label';

import { Account, AccountNumber } from '../../../../generated-types';
import useTranslation from '../../../../hooks/useTranslation';
import { AccountLabels } from '../../../shared/AccountLabels';
import { currencyFormatter } from '../../../shared/numberFormat';
import { isUnknownAccount } from '../../../transaction/Transfer/hooks/useToAccount';

import './FromAccountSelect.scss';

interface Props {
    selected?: AccountNumber;
    id: string;
    placeholder: string;
    showNegativeAmount: boolean;
    label?: string;
    tabIndex?: number;
    open: boolean;
    focusFirst: boolean;
    selectRef: React.MutableRefObject<HTMLButtonElement | null>;
    selectedAccount?: Account;
    locked: boolean;
    formattedAccountNumber: string;
    setOpen: (value: boolean) => void;
    selectKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
    isBlocked?: boolean;
}

const SelectedAccountButton = ({
    selected,
    selectRef,
    label,
    formattedAccountNumber,
    open,
    id,
    setOpen,
    placeholder,
    tabIndex,
    locked,
    selectedAccount,
    showNegativeAmount,
    selectKeyDown,
    focusFirst,
    isBlocked = false,
}: Props) => {
    const translated = useTranslation();
    return (
        <>
            <span className="from-account-select__label-text" id={`combo-label-${id}`}>
                {label && <span>{label}</span>}
                {(selectedAccount || isBlocked) && (
                    <span className="from-account-select__list-item-owners">
                        {!isBlocked ? (
                            <AccountLabels
                                owners={selectedAccount?.owners ?? []}
                                className="account-button__owner-label"
                            />
                        ) : (
                            <ErrorLabel
                                size="small"
                                className="account-button__owner-label"
                                text={translated.accountIsBlocked}
                            />
                        )}
                    </span>
                )}
            </span>
            <button
                id={id}
                aria-haspopup="listbox"
                aria-expanded={open}
                className={classNames('from-account-select__element', { active: open })}
                onKeyDown={selectKeyDown}
                disabled={locked}
                type="button"
                ref={selectRef}
                onClick={() => setOpen(!open)}
                tabIndex={tabIndex}
                data-focus-first={focusFirst ? true : undefined}
                data-testid="from-account-select__desktop-button"
            >
                <span className={classNames('from-account-select__left-text', { selected })}>
                    {selectedAccount ? selectedAccount?.alias : (selected && formattedAccountNumber) ?? placeholder}
                </span>
            </button>
            <span
                className={classNames('from-account-select__left-sub-text', {
                    empty: formattedAccountNumber.length === 0,
                })}
                data-testid="from-account-number-selected"
            >
                {selected ? formattedAccountNumber : ''}
            </span>
            <span className="from-account-select__right-text" data-testid="selected-from-account-balance">
                {selectedAccount && !isUnknownAccount(selectedAccount)
                    ? currencyFormatter(
                          showNegativeAmount
                              ? selectedAccount.balance.accountingBalance
                              : selectedAccount.balance.availableBalance,
                          selectedAccount.currency,
                      )
                    : ''}
            </span>
            <span className="from-account-select__icon">
                {locked ? <Icon type={IconTypes.PIN_KODE_24} /> : <Icon type={IconTypes.LITEN_PIL_NED} />}
            </span>
        </>
    );
};

export default SelectedAccountButton;
